
import BannerController                 from '_controllers/BannerController';
import DialogModalController            from '_controllers/modals/DialogModalController';
import DomHelper                        from '_utils/DomHelper';
import EventManager                     from '@brainscape/event-manager';
import packRelatedSubject               from '_models/packRelatedSubject';    
import React                            from 'react';
import ReactHelper                      from '_utils/ReactHelper';
import RelatedSubjectsBannerController    from '_controllers/RelatedSubjectsBannerController';
import TooltipController                from '_controllers/TooltipController';
import SearchBar                        from '_views/shared/SearchBar';
import showdown                         from "showdown";
import SpacedRepetitionModalController  from '_controllers/modals/SpacedRepetitionModalController';
import UiHelper                         from '_utils/UiHelper';


const markdownConverter = new showdown.Converter();
markdownConverter.setOption('simpleLineBreaks', true);

let bodyElem,
    bccBanner,
    flashcardsPageElem,
    confidenceScaleElems,
    deckCreator,
    flashcardsSearchBar,
    unblurredFlashcards,
    deckTitle,
    selectedConfidenceLevel,
    thinCards,

    isDesktopView,
    isMobileView,
    isCurrentUser,
    isGuest,
    isBlurrable;

const deckCardsPage = {
  init() {

    /*
    ==================================================
     CACHE DOM ELEMENTS
    ==================================================
    */

    bodyElem = document.querySelector('body');
    flashcardsPageElem = document.querySelector('.flashcards-page');
    thinCards = document.querySelectorAll('.flashcard-row.thin-card');

    if (bodyElem) {
      bccBanner = bodyElem.querySelector('#related-subjects-banner-controller');
      isCurrentUser = bodyElem.classList.contains('is-current-user'); 
      isGuest = bodyElem.classList.contains('is-guest'); 
      isDesktopView = bodyElem.classList.contains('desktop-web'); 
      isMobileView = bodyElem.classList.contains('mobile-web'); 
      isBlurrable = bodyElem.classList.contains('is-blurrable'); 
    }

    if (flashcardsPageElem) {
      confidenceScaleElems = flashcardsPageElem.querySelectorAll('.confidence-scale');
      deckCreator = flashcardsPageElem.querySelector('.flashcards-page .header .title span');
      flashcardsSearchBar = flashcardsPageElem.querySelector('#flashcards-search-bar');
      unblurredFlashcards = flashcardsPageElem.querySelectorAll('.flashcard-row:not(.blurred-content)');
      deckTitle = flashcardsPageElem.querySelectorAll('.flashcards-sidebar .deck-list-items .deck-link .deck-list-item'); 
      selectedConfidenceLevel;
    }
    
    /*
    ==================================================
     RENDER REACT COMPONENTS
    ==================================================
    */

    ReactHelper.renderComponent(<TooltipController />, '#tooltip-controller');

    ReactHelper.renderComponent(<SpacedRepetitionModalController />, '#spaced-repetition-modal-controller');

    ReactHelper.renderComponent(<DialogModalController />, '#dialog-modal-controller');

    ReactHelper.renderComponent(<BannerController />, '#banner-controller');

    ReactHelper.renderComponent(<RelatedSubjectsBannerController />, '#related-subjects-banner-controller');

    if (flashcardsSearchBar) {
      ReactHelper.renderComponent(<SearchBar
        shouldSuppressInitialFocus={true}
        placeholder={'Search Brainscape'}
      />, '#flashcards-search-bar');
    }

    /*
    ==================================================
    SETUP DOM ELEMENTS
    ==================================================
    */

    this.wireThinCards();
    this.wireUnblurredGuestFlashcards();
    this.wireConfidenceScaleElems();
    this.wireDeckCreator();
    this.wireDeckTitle();
    this.wireSoundButtons();
    
    this.manageRelatedSubjectsBanner();
    this.scrollToCardAfterPageLoad();
  },


  /*
  ==================================================
    WIRE BEHAVIORS
  ==================================================
  */

  wireConfidenceScaleElems() {
    if (confidenceScaleElems?.length > 0) {
      confidenceScaleElems.forEach(elem => {
        elem.addEventListener('click', e => this.handleConfidenceRating(e));
      });
    }
  },

  wireDeckCreator() {
    if (deckCreator) {
      const tooltipContent = 'By ' + deckCreator.getAttribute('title');
      const elem = deckCreator;

      if (window.innerWidth > 942) {
        this.wireElemTooltip(elem, tooltipContent, 'top');
      } else {
        deckCreator.addEventListener("click", (e) => {
          this.handleMobileCreatorClick();
        });
      }
    }
  },

  wireDeckTitle() {
    if (deckTitle) {
      deckTitle.forEach(title => {
        if (this.isTitleTruncated(title)) {
          const tooltipContent = title.innerHTML;
          const elem = title;
          this.wireElemTooltip(elem, tooltipContent, 'right');
        }
      });
    }
  },

  wireElemTooltip(elem, tooltipContent, position) {
    elem.addEventListener("mouseenter", () => {
      this.triggerTooltipOpen({
        content: tooltipContent,
        elem: elem,
        position: position,
      });
    });
  
    elem.addEventListener("mouseleave", () => {
      this.triggerTooltipClose();
    });
  },

  wireThinCards() {
    if (thinCards?.length > 0) {
      thinCards.forEach(thinCardDiv => {
        this.setIntersectionObserver(thinCardDiv, (entries, observer) => this.handleThinCardIntersection(entries, observer));
      });
    }
  },

  wireUnblurredDesktopFlashcards() {
    let timeoutId = null;

    unblurredFlashcards.forEach(flashcard => {
      const matchedElement = flashcard.querySelector('.flashcard-inner-contents');
      
      if (matchedElement) {
        UiHelper.onLinger(matchedElement, (e) => {
          this.handleUnblurredDesktopFlashcardLinger(e, matchedElement);
        });
      }
    
      flashcard.addEventListener("mouseenter", () => {
        clearTimeout(timeoutId);
      });

      flashcard.addEventListener("mouseleave", () => {
        clearTimeout(timeoutId);
    
        timeoutId = setTimeout(() => {
          this.resetConfidenceScales();
        }, 500);
      });
    });
  },

  wireUnblurredGuestFlashcards() {
    if (!isGuest) {
      return false;
    }

    if (isMobileView) {
      this.wireUnblurredMobileFlashcards();
    } else {
      this.wireUnblurredDesktopFlashcards();
    }
  },

  wireUnblurredMobileFlashcards() {
    unblurredFlashcards.forEach(elem => {
      elem.addEventListener('click', e => this.handleUnblurredMobileFlashcardClick(e));
    });
  },


  /*
  ==================================================
   EVENT HANDLERS
  ==================================================
  */

  handleConfidenceRating(e) {
    this.setConfidenceLevel(e)
    .then((result) => {
      selectedConfidenceLevel = result;
      setTimeout(() => {
        this.showSpacedRepetitionModal(selectedConfidenceLevel);
      }, 500);
    })
    .catch(error => {
      // Err
    });
  },

  handleMobileCreatorClick() {
    const deckTitle = deckCreator.getAttribute('title');
    const isCertified = deckTitle == "Brainscape";
  
    const heading = isCertified ? 'Brainscape Certified!' : 'User Generated Class';
    const body = isCertified
    ? (
        <>
          When you see this distinctive icon accompanying a class name, you can be confident that you're accessing top-tier, expert-curated content.
        </>
      )
    : (
        <>
          This class was created <strong>{deckTitle}</strong>.
        </>
      );
  
    const imageType = isCertified ? 'certified-icon' : 'user-generated-icon';
  
    this.triggerDialogModalOpen(heading, body, imageType);
  },

  handleThinCardIntersection(entries, observer) {
    entries.forEach(entry => {
      if (entry.isIntersecting) {
        const cardDiv = entry.target;

        if (cardDiv.classList.contains('is-hydrated')) {
          return false;
        }
    
        this.hydrateCard(cardDiv);
      }
    });
  },

  handleUnblurredMobileFlashcardClick(e) {
    this.resetConfidenceScales();

    const matchedElement = e.target.closest('.flashcard-inner-contents');
    const confidenceScaleElement = matchedElement ? matchedElement.parentNode.querySelector('.confidence-scale') : null;

    if (confidenceScaleElement) {
        confidenceScaleElement.classList.add('is-active');
    }
  },

  handleUnblurredDesktopFlashcardLinger(e, matchedElement) {
    this.resetConfidenceScales();

    const tooltipId = 'confidence-scale-' + matchedElement.id;
    const isMouseOverTooltip = e.relatedTarget && e.relatedTarget.closest(`#${tooltipId}`);
    const confidenceScaleElement = matchedElement.parentNode.querySelector('.confidence-scale');

    if (confidenceScaleElement) {
      confidenceScaleElement.classList.add('is-active');
    }

    if (!isMouseOverTooltip) {
      e.stopPropagation();
    }
  },


  /*
  ==================================================
   EVENT TRIGGERS
  ==================================================
  */

  triggerDialogModalOpen(heading, body, imageType){
    EventManager.emitEvent('dialog-modal:open', {
      heading: heading,
      body: body,
      imageType: imageType,
    });
  },

  triggerRelatedSubjectsBannerOpen(relatedSubjects) {
    EventManager.emitEvent('related-subjects-banner:open', {
      addClasses:       'related-subjects-banner',
      id:               'bcc-banner',
      relatedSubjects: relatedSubjects,
    });
  },

  triggerSpacedRepetitionModalClose() {
    EventManager.emitEvent('spaced-repetition-modal:close', {});
  },

  triggerSpacedRepetitionModalOpen(viewProps) {
    EventManager.emitEvent('spaced-repetition-modal:open', viewProps);
  },

  triggerTooltipClose(){
    EventManager.emitEvent('tooltip:close', {});
  },

  triggerTooltipOpen(opts){
    EventManager.emitEvent('tooltip:open', {
      content: opts.content,
      elem: opts.elem,
      position: opts.position,
    });
  },


  /*
  ==================================================
   SOUND PLAYER UTILS
  ==================================================
  */

  wireSoundButtons() {
    const soundButtons = document.querySelectorAll(
      '.flashcards-page .flashcard-sound-buttons',
    );

    if (soundButtons && soundButtons.length > 0) {
      soundButtons.forEach((soundButtonPair) => {
        this.wireSoundButtonPair(soundButtonPair);
      });
    }
  },

  wireSoundButtonPair(soundButtonPair) {
    soundButtonPair.addEventListener('click', (e) => {
      this.handleSoundButtonClick(e);
    });
  },

  handleSoundButtonClick(e) {
    e.stopPropagation();

    const clickedSoundButton = DomHelper.getSelfOrAncestor(
      e.target,
      '.flashcard-sound-buttons',
    );

    const selfCardHeaderElem = DomHelper.getAncestor(
      clickedSoundButton,
      '.flashcard-header',
    );

    if (selfCardHeaderElem.classList.contains('sound-active')) {
      // user hit pause on current card face's playing audio
      this.pausePlayer(selfCardHeaderElem);

      return true;
    }

    const activeCardElems = document.querySelectorAll(
      '.flashcard-header.sound-active',
    );

    // stop any currently playing sound elements
    if (activeCardElems && activeCardElems.length > 0) {
      activeCardElems.forEach((activeCardElem) => {
        this.stopPlayer(activeCardElem);
      });
    }

    // start new player
    this.startPlayer(clickedSoundButton);
  },

  pausePlayer(activeCardElem) {
    const playerElem = activeCardElem.querySelector('.card-audio-player');

    if (playerElem) {
      playerElem.pause();
      this.removeSoundActiveFlag(playerElem);
    }
  },

  stopPlayer(activeCardElem) {
    const playerElem = activeCardElem.querySelector('.card-audio-player');

    if (playerElem) {
      playerElem.pause();
      playerElem.currentTime = 0;
      this.removeSoundActiveFlag(playerElem);
    }
  },

  startPlayer(clickedSoundButton) {
    const playerElem = DomHelper.getNextSibling(
      clickedSoundButton,
      '.card-audio-player',
    );

    if (playerElem) {
      playerElem.play();
      this.addSoundActiveFlag(playerElem);
    }
  },

  addSoundActiveFlag(playerElem) {
    const playerCardHeader = DomHelper.getAncestor(
      playerElem,
      '.flashcard-header',
    );

    if (playerCardHeader) {
      playerCardHeader.classList.add('sound-active');
    }
  },

  removeSoundActiveFlag(playerElem) {
    const playerCardHeader = DomHelper.getAncestor(
      playerElem,
      '.flashcard-header',
    );

    if (playerCardHeader) {
      playerCardHeader.classList.remove('sound-active');
    }
  },


  /*
  ==================================================
   LOCAL UTILS
  ==================================================
  */

  buildSoundControlsHeader(soundUrl, face, cardNumber) {
    // used in hydrating audio buttons for thin cards
    if (!soundUrl) return null;

    const flashcardHeader = document.createElement('div');
    flashcardHeader.classList.add('flashcard-header');

    const flashcardControls = document.createElement('div');
    flashcardControls.classList.add('flashcard-controls');

    const soundButtons = document.createElement('div');
    soundButtons.classList.add('flashcard-sound-buttons');

    const playButton = document.createElement('div');
    playButton.classList.add('icon-button', 'play-button', 'play-sound-button');
    const playIcon = document.createElement('div');
    playIcon.classList.add('icon', 'ion-ios-play');
    playButton.appendChild(playIcon);

    const pauseButton = document.createElement('div');
    pauseButton.classList.add('icon-button', 'pause-button', 'pause-sound-button');
    const pauseIcon = document.createElement('div');
    pauseIcon.classList.add('icon', 'ion-ios-pause');
    pauseButton.appendChild(pauseIcon);

    soundButtons.appendChild(playButton);
    soundButtons.appendChild(pauseButton);
    this.wireSoundButtonPair(soundButtons);

    const audioPlayer = document.createElement('audio');
    audioPlayer.classList.add('card-audio-player');
    audioPlayer.id = `sound-player-${cardNumber}-${face}`;

    const audioSource = document.createElement('source');
    audioSource.src = soundUrl;
    audioPlayer.appendChild(audioSource);

    flashcardControls.appendChild(soundButtons);
    flashcardControls.appendChild(audioPlayer);

    flashcardHeader.appendChild(flashcardControls);

    return flashcardHeader;
  },

  convertMarkdownToHtml(markdownText) {
    // Fix missing blank line before lists
    markdownText = markdownText.replace(/([^\n])\n(\s*[*-]\s)/g, "$1\n\n$2");
  
    // Convert Markdown to HTML using Showdown
    return markdownConverter.makeHtml(markdownText);
  },
  
  extractFootnote(text) {
    const footnoteRegex = /## Footnote\s*([\s\S]*)/i;
    const match = text.match(footnoteRegex);
  
    if (match) {
      return {
        mainText: text.replace(footnoteRegex, "").trim(),
        footnoteText: match[1].trim(),
      };
    }
  
    return { mainText: text, footnoteText: "" };
  }, 
  
  extractPrompt(text) {
    const promptRegex = /^#\s+([^\n]+)/;
    const match = text.match(promptRegex);
  
    if (match) {
      return {
        promptText: match[1].trim(),
        bodyText: text.replace(promptRegex, "").trim(), // Remove the heading from main text
      };
    }
  
    return { promptText: "", bodyText: text };
  },

  getRelatedSubjects(packId) {
    return new Promise((resolve, reject) => {

      if (!bccBanner) {
        reject(new Error("No related subjects banner found"));
        return;
      }

      const view = 'banner';
      const urlParams = new URLSearchParams(window.location.search);
      const forceRelatedSubjects = urlParams.get('force_related_subjects') == 'true';

      packRelatedSubject.index(packId, view, forceRelatedSubjects)
        .then(rs => {
          resolve(rs || []);
        })
        .catch(err => {
          console.error('Error fetching related subjects:', err);
          reject(err);
        });
    });
  },

  hydrateCard(cardDiv) {
    if (cardDiv.classList.contains('is-hydrated')) {
      return false;
    }

    this.hydrateSubscribeLinkButton(cardDiv);
    this.hydrateAudioButtons(cardDiv);
    this.processMarkdownContent(cardDiv);

    cardDiv.classList.add('is-hydrated');
  },

  hydrateSubscribeLinkButton(cardDiv) {
    if (!(isBlurrable && cardDiv.classList.contains('is-blurrable'))) {
      return false;
    }

    const answerDiv = cardDiv.querySelector('.answer');

    // Prevent duplicate button insertions
    if (!answerDiv.querySelector('.subscribe-link')) {
      const aButtonUrl = cardDiv.dataset.aButtonUrl;

      // Create anchor element
      const link = document.createElement('a');
      link.className = "subscribe-link rect-button primary-cta tertiary blurred-answer-study-cta";
      link.href = aButtonUrl;
      link.textContent = 'Study These Flashcards';

      // Append the link to the answerDiv
      answerDiv.appendChild(link);
    }
  },

  hydrateAudioButtons(cardDiv) {
    if (cardDiv.classList.contains('has-audio-buttons')) {
      return false;
    }

    const qSoundUrl = cardDiv.dataset.qSoundUrl;
    const aSoundUrl = cardDiv.dataset.aSoundUrl;
    const cardNumber = cardDiv.dataset.number;  // Assuming card number is stored here

    if (qSoundUrl || aSoundUrl) {
      cardDiv.classList.add('has-audio-buttons');
    }

    // Append sound buttons to question face if qSoundUrl exists
    if (qSoundUrl) {
      const questionFace = cardDiv.querySelector('.card-face.question');
      if (questionFace) {
        const questionSoundControlsHeader = this.buildSoundControlsHeader(qSoundUrl, 'question', cardNumber);
        questionFace.appendChild(questionSoundControlsHeader);
      }
    }

    // Append sound buttons to answer face if aSoundUrl exists
    if (aSoundUrl && !cardDiv.classList.contains('is-blurrable')) {    
      const answerFace = cardDiv.querySelector('.card-face.answer');
      if (answerFace) {
        const answerSoundControlsHeader = this.buildSoundControlsHeader(aSoundUrl, 'answer', cardNumber);
        answerFace.appendChild(answerSoundControlsHeader);
      }
    }
  },

  isTitleTruncated (title) {
    return title.scrollWidth > title.clientWidth;
  },

  manageRelatedSubjectsBanner() {
    if (isDesktopView && bccBanner) {
      const hasRelatedSubjects = bccBanner?.dataset?.hasRelatedSubjects;
      const packId = bccBanner?.dataset?.packId;

      if (!(hasRelatedSubjects && packId)) return false;

      setTimeout(() => {
        this.getRelatedSubjects(packId)
          .then((relatedSubjects) => {
            if (relatedSubjects.length > 0) {
              this.triggerRelatedSubjectsBannerOpen(relatedSubjects);
            }
          })
          .catch((error) => {
            console.error('Error fetching related subjects:', error);
          });
      }, 2000);
    }
  },

  processMarkdownContent(cardDiv) {
    ["question", "answer"].forEach((face) => {
      const faceElem = cardDiv.querySelector(`.card-face.${face}`);
  
      if (faceElem) {
        // Locate the text-containing div
        const contentElem = face === "question"
          ? faceElem.querySelector(".question-content")  // Newly added div for questions
          : faceElem.querySelector(".answer-content");   // Already exists in the HAML
  
        if (!contentElem) {
          console.warn(`No dedicated content container found for ${face}, skipping.`);
          return;
        }
  
        const rawText = contentElem.textContent.trim();
        
        // Extract heading and footnote
        const { mainText, footnoteText } = this.extractFootnote(rawText);
        const { bodyText, promptText } = this.extractPrompt(mainText);
  
        // Convert main text from Markdown to HTML
        const processedHtml = this.convertMarkdownToHtml(bodyText);
  
        // Clear existing text (only the text inside `contentElem`)
        contentElem.innerHTML = "";
  
        // Append the prompt div if a heading was found
        if (promptText) {
          const promptDiv = document.createElement("div");
          promptDiv.classList.add("prompt");
          promptDiv.innerHTML = this.convertMarkdownToHtml(promptText);
          contentElem.appendChild(promptDiv);
        }
  
        // Append the processed main text
        const bodyDiv = document.createElement("div");
        bodyDiv.innerHTML = processedHtml;
        contentElem.appendChild(bodyDiv);
  
        // Find any existing image inside faceElem (outside contentElem)
        const imageElem = faceElem.querySelector("img.card-image");
        if (imageElem) {
          contentElem.appendChild(imageElem);
        }
  
        // Append the footnote if present
        if (footnoteText) {
          const footnoteDiv = document.createElement("div");
          footnoteDiv.classList.add("footnote");
          footnoteDiv.innerHTML = this.convertMarkdownToHtml(footnoteText);
          contentElem.appendChild(footnoteDiv);
        }
      }
    });
  },
      
  resetConfidenceScales() {
    const allConfidenceScales = document.querySelectorAll('.flashcards-page .flashcard-row .confidence-scale');
    allConfidenceScales.forEach((confidenceScale) => {
      confidenceScale.classList.remove('is-active');
    });
  },

  scrollToCardAfterPageLoad() {
    const scrollDelay = setTimeout(() => {
      const params = new URLSearchParams(document.location.search);
      const cardId = params.get('cardId');

      if (cardId) {
        const cardElem = document.getElementById(`card-${cardId}`);

        if (cardElem) {
          cardElem.scrollIntoView({ block: 'end', inline: 'start',  behavior: 'smooth' });
        }
      }
    }, 1000);
  },

  setConfidenceLevel(e) {
    return new Promise((resolve, reject) => {
      const matchedElement = e.target.closest('.confidence-scale .confidence-button');
  
      if (!matchedElement) {
        reject(new Error("No matching element found"));
        return;
      }
  
      const matchedElementParent = matchedElement.closest('.flashcard-row');
      const levelClasses = Array.from(matchedElement.classList).filter(className => className.includes('level'));
      const existingLevelClass = Array.from(matchedElementParent.classList).find(className => className.includes('level'));
  
      if (existingLevelClass) {
        matchedElementParent.classList.remove(existingLevelClass);
      }
  
      matchedElementParent.classList.add(...levelClasses);
  
      // resolve('Confidence level set successfully');
      resolve(matchedElementParent);
    });
  },

  setIntersectionObserver(target, handleIntersection) {
    const observer = new IntersectionObserver(handleIntersection);
    observer.observe(target);
  },

  showSpacedRepetitionModal(selectedConfidenceLevel) {
    const confidenceLevelSelected = [...selectedConfidenceLevel.classList]
    .filter(className => className.includes('level'))
    .join('');

    this.triggerSpacedRepetitionModalOpen({
      confidenceLevel: confidenceLevelSelected,
    });
  },
};

export default deckCardsPage;
